/**
 * IntroCarousel — the first-access onboarding carousel of business-web. (Its
 * journey-web twin is retired: the citizen's Welcome home tells that story.)
 *
 * Presentation-only per @tonnie/ui rules: NO i18n, NO @tonnie/sdk, NO asset or
 * design-token imports. The consumer resolves each slide to a view-model
 * (`IntroCarouselSlideVM`) — avatar node, accent hex, translated strings — and
 * passes labels + handlers. This component owns the experience: fixed-size
 * modal (only the interior swaps between slides), immersive accent backdrop,
 * segmented progress, choreographed reveal, and the full interaction +
 * accessibility set (swipe, keyboard ←/→, Back/Next, tap-to-jump, Esc/skip,
 * focus-to-CTA on the final slide, reduced-motion aware).
 *
 * Motion is pure CSS (see KEYFRAMES) so this adds no runtime dependency.
 */

import * as React from 'react';
import { cn } from '../lib/utils';
import { Dialog, DialogContent, DialogTitle } from '../primitives/dialog';

export interface IntroCarouselSlideVM {
  /** Stable id — drives the E2E test-id and the reveal key. */
  id: string;
  /**
   * The slide's accent as a fill (buttons, progress, the backdrop tints) — a CSS
   * colour the consumer sources from design tokens (e.g. `HERO_COLORS[hero].primary`).
   */
  accent: string;
  /** Text on the `accent` fill (`HERO_COLORS[hero].onPrimary`). */
  onAccent: string;
  /** The accent as text on the page (`HERO_COLORS[hero].text`). */
  accentText: string;
  /** The hero avatar to render (resolved node, e.g. `<HeroAvatar …/>`). */
  avatar: React.ReactNode;
  /** Translated eyebrow / title / body. */
  eyebrow: string;
  title: string;
  body: string;
}

export interface IntroCarouselLabels {
  /** Accessible dialog title (visually hidden). */
  title: string;
  next: string;
  back: string;
  /** Screen-reader label for the corner close (X) and the skip affordance. */
  skip: string;
  cta: string;
  ctaSecondary: string;
  /** Per-segment aria-label, e.g. `(n) => t('introCarousel.goToSlide',{number:n})`. */
  goToSlide: (oneBasedIndex: number) => string;
}

export interface IntroCarouselProps {
  open: boolean;
  slides: readonly IntroCarouselSlideVM[];
  labels: IntroCarouselLabels;
  /** Dismiss + persist "seen" — X, Esc, overlay, and the secondary CTA. */
  onDismiss: () => void;
  /** Primary CTA on the final slide (the consumer also persists "seen"). */
  onCta: () => void;
  /**
   * Hero visual treatment. `'avatar'` (default) seats each `slide.avatar` node
   * in a round, full-bleed character frame (citizen talent tour). `'icon'`
   * seats it in a squircle accent-tinted **enterprise tile** — the icon inherits
   * the accent via `currentColor` — for the icon-forward B2B tour (business-web).
   */
  visual?: 'avatar' | 'icon';
}

const KEYFRAMES = `
@keyframes tnIntroSlideNext { from { opacity: 0; transform: translateX(26px); } to { opacity: 1; transform: none; } }
@keyframes tnIntroSlidePrev { from { opacity: 0; transform: translateX(-26px); } to { opacity: 1; transform: none; } }
@keyframes tnIntroRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes tnIntroPop { from { opacity: 0; transform: scale(.9); } to { opacity: 1; transform: none; } }
@keyframes tnIntroFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes tnIntroDrift1 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(22px,16px) scale(1.12); } }
@keyframes tnIntroDrift2 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-18px,22px) scale(1.08); } }
.tn-intro-slide { animation: tnIntroSlideNext .4s cubic-bezier(.2,.7,.2,1) both; }
.tn-intro[data-dir="prev"] .tn-intro-slide { animation-name: tnIntroSlidePrev; }
.tn-intro-avatar { animation: tnIntroPop .42s cubic-bezier(.2,.7,.2,1) both, tnIntroFloat 5.6s ease-in-out .42s infinite; }
.tn-intro-kick { animation: tnIntroRise .5s cubic-bezier(.2,.7,.2,1) both; animation-delay: .1s; }
.tn-intro-title { animation: tnIntroRise .5s cubic-bezier(.2,.7,.2,1) both; animation-delay: .16s; }
.tn-intro-body { animation: tnIntroRise .5s cubic-bezier(.2,.7,.2,1) both; animation-delay: .23s; }
.tn-intro-blob1 { animation: tnIntroDrift1 14s ease-in-out infinite; }
.tn-intro-blob2 { animation: tnIntroDrift2 17s ease-in-out infinite; }
.tn-intro-blob3 { animation: tnIntroDrift1 20s ease-in-out infinite reverse; }
@media (prefers-reduced-motion: reduce) {
  .tn-intro-slide, .tn-intro-avatar, .tn-intro-kick, .tn-intro-title, .tn-intro-body { animation: none !important; }
  .tn-intro-blob1, .tn-intro-blob2, .tn-intro-blob3 { animation: none !important; }
}
`;

export function IntroCarousel({
  open,
  slides,
  labels,
  onDismiss,
  onCta,
  visual = 'avatar',
}: IntroCarouselProps) {
  const isIcon = visual === 'icon';
  const [selected, setSelected] = React.useState(0);
  const [dir, setDir] = React.useState<'next' | 'prev'>('next');
  const ctaRef = React.useRef<HTMLButtonElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const pointerRef = React.useRef<{ x: number; t: number } | null>(null);

  const total = slides.length;
  const active = slides[selected] ?? slides[0];
  const isFinal = selected >= total - 1;

  // Reset to the first slide whenever the carousel (re)opens.
  React.useEffect(() => {
    if (open) {
      setSelected(0);
      setDir('next');
    }
  }, [open]);

  const go = React.useCallback(
    (n: number) => {
      setSelected((cur) => {
        const next = Math.max(0, Math.min(total - 1, n));
        if (next === cur) return cur;
        setDir(next > cur ? 'next' : 'prev');
        return next;
      });
    },
    [total]
  );

  // Focus the primary CTA when the final slide appears (interaction moves off Next).
  React.useEffect(() => {
    if (open && isFinal) {
      const id = window.setTimeout(() => ctaRef.current?.focus(), 60);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, isFinal]);

  // Keyboard (←/→) + swipe/drag, attached imperatively while open — works
  // regardless of where focus sits and keeps these listeners off a
  // non-interactive element.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(selected + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(selected - 1);
      }
    };
    const el = rootRef.current;
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      pointerRef.current = { x: e.clientX, t: Date.now() };
    };
    const onUp = (e: PointerEvent) => {
      const start = pointerRef.current;
      pointerRef.current = null;
      if (!start) return;
      const dx = e.clientX - start.x;
      if (Math.abs(dx) > 44 && Date.now() - start.t < 700) go(selected + (dx < 0 ? 1 : -1));
    };
    document.addEventListener('keydown', onKey);
    el?.addEventListener('pointerdown', onDown);
    el?.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('keydown', onKey);
      el?.removeEventListener('pointerdown', onDown);
      el?.removeEventListener('pointerup', onUp);
    };
  }, [open, selected, go]);

  if (!active) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDismiss()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="w-full gap-0 overflow-hidden rounded-2xl border-0 p-0 shadow-2xl sm:max-w-md"
        data-testid="intro-carousel"
        data-active-index={selected}
      >
        <style>{KEYFRAMES}</style>
        <DialogTitle className="sr-only">{labels.title}</DialogTitle>

        <div
          ref={rootRef}
          className="tn-intro bg-background relative flex h-[560px] max-h-[85vh] flex-col select-none"
          data-dir={dir}
          style={
            {
              '--accent': active.accent,
            } as React.CSSProperties
          }
          role="group"
          aria-roledescription="carousel"
        >
          {/* Immersive accent aurora */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
            <span
              className="tn-intro-blob1 absolute -left-10 -top-16 block h-72 w-72 rounded-full blur-[46px]"
              style={{
                background:
                  'radial-gradient(circle, color-mix(in srgb, var(--accent) 52%, transparent), transparent 70%)',
              }}
            />
            <span
              className="tn-intro-blob2 absolute -right-16 top-24 block h-64 w-64 rounded-full blur-[46px]"
              style={{
                background:
                  'radial-gradient(circle, color-mix(in srgb, var(--accent) 40%, transparent), transparent 70%)',
              }}
            />
            <span
              className="tn-intro-blob3 absolute -bottom-16 left-[38%] block h-56 w-56 rounded-full blur-[46px]"
              style={{
                background:
                  'radial-gradient(circle, color-mix(in srgb, var(--accent) 30%, transparent), transparent 70%)',
              }}
            />
          </div>

          {/* Corner close (dismiss) */}
          <button
            type="button"
            onClick={onDismiss}
            aria-label={labels.skip}
            data-testid="intro-carousel-skip"
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute right-4 top-4 z-20 grid h-8 w-8 place-items-center rounded-full border border-border/60 bg-background/70 backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Segmented progress = tablist / jump control */}
          <div
            className="relative z-10 flex gap-1.5 px-6 pt-6"
            role="tablist"
            aria-label={labels.title}
          >
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={i === selected}
                aria-label={labels.goToSlide(i + 1)}
                onClick={() => go(i)}
                data-testid={`intro-carousel-dot-${i}`}
                className="group relative h-1 flex-1 overflow-hidden rounded-full bg-muted-foreground/20"
              >
                <span
                  className="block h-full rounded-full transition-[width] duration-500"
                  style={{ width: i <= selected ? '100%' : '0%', backgroundColor: active.accent }}
                />
              </button>
            ))}
          </div>

          {/* Announce active slide to assistive tech */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {active.title}
          </div>

          {/* Slide area — flexes so copy length never changes the modal size */}
          <div className="relative z-10 flex flex-1 items-center overflow-hidden">
            <div
              key={active.id}
              data-testid={`intro-carousel-slide-${active.id}`}
              className="tn-intro-slide flex w-full flex-col items-center px-8 text-center"
            >
              {/* Fixed hero-visual frame — every slide's visual seats identically.
                  `avatar` = round full-bleed character; `icon` = squircle accent
                  tile whose icon inherits the accent via currentColor. */}
              <div className="relative mb-6 grid h-[136px] w-[136px] place-items-center">
                <span
                  aria-hidden="true"
                  className={cn('absolute inset-0', isIcon ? 'rounded-[32px]' : 'rounded-full')}
                  style={{
                    background:
                      'radial-gradient(circle at 50% 44%, color-mix(in srgb, var(--accent) 44%, transparent), transparent 68%)',
                  }}
                />
                <div
                  className={cn(
                    'tn-intro-avatar relative grid place-items-center',
                    isIcon
                      ? 'h-[116px] w-[116px] rounded-[30px]'
                      : 'h-[136px] w-[136px] overflow-hidden rounded-full'
                  )}
                  style={
                    isIcon
                      ? {
                          // Icon inherits this via currentColor. Mixed toward
                          // `--foreground` (light in dark mode, dark in light) so a
                          // deep accent stays legible on the tinted tile in BOTH
                          // themes — theme-aware without a dark selector.
                          color: 'color-mix(in srgb, var(--accent) 74%, var(--foreground) 26%)',
                          background:
                            'linear-gradient(160deg, color-mix(in srgb, var(--accent) 22%, var(--background)), color-mix(in srgb, var(--accent) 7%, var(--background)))',
                          boxShadow:
                            '0 20px 40px -18px color-mix(in srgb, var(--accent) 50%, transparent), inset 0 0 0 1px color-mix(in srgb, var(--accent) 38%, transparent)',
                        }
                      : {
                          boxShadow:
                            '0 22px 42px -16px color-mix(in srgb, var(--accent) 55%, transparent), 0 0 0 4px color-mix(in srgb, var(--accent) 20%, transparent)',
                        }
                  }
                >
                  {active.avatar}
                </div>
              </div>

              <p
                className="tn-intro-kick mb-3 text-xs font-semibold uppercase tracking-[0.14em]"
                style={{ color: active.accentText }}
              >
                {active.eyebrow}
              </p>
              <h2
                data-testid="intro-carousel-slide-title"
                className="tn-intro-title text-foreground mb-2 text-balance text-xl font-bold tracking-tight"
              >
                {active.title}
              </h2>
              <p className="tn-intro-body text-muted-foreground max-w-xs text-sm leading-relaxed">
                {active.body}
              </p>
            </div>
          </div>

          {/* Footer nav — Back/Next, or CTA on the final slide */}
          <div className="relative z-10 px-6 pb-7 pt-3">
            {isFinal ? (
              <div className="flex flex-col gap-1">
                <button
                  ref={ctaRef}
                  type="button"
                  onClick={onCta}
                  data-testid="intro-carousel-cta"
                  className="focus-visible:ring-ring inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl text-sm font-bold transition-[filter] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ backgroundColor: active.accent, color: active.onAccent }}
                >
                  {labels.cta}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  data-testid="intro-carousel-cta-secondary"
                  className="text-muted-foreground hover:text-foreground h-10 w-full rounded-xl text-sm font-semibold transition-colors"
                >
                  {labels.ctaSecondary}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => go(selected - 1)}
                  data-testid="intro-carousel-back"
                  className={cn(
                    'text-foreground hover:bg-muted focus-visible:ring-ring inline-flex h-11 items-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
                    selected === 0 && 'invisible'
                  )}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M11 6l-6 6 6 6" />
                  </svg>
                  {labels.back}
                </button>
                <button
                  type="button"
                  onClick={() => go(selected + 1)}
                  data-testid="intro-carousel-next"
                  className="focus-visible:ring-ring inline-flex h-11 items-center gap-1.5 rounded-xl px-5 text-sm font-bold transition-[filter] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ backgroundColor: active.accent, color: active.onAccent }}
                >
                  {labels.next}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
